import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useGuidedPlanningQuotes } from './use-guided-planning-quotes';

import { enrichQuotesWithROI, formatPercent } from '@shared';
import { formatROI, IRR_SUSPECT_MESSAGE } from '@shared/formatROI';

import { selectClient } from '@/model';
import { useSession } from '@/features/guided-financial-planning/context';

export interface TopPolicyIrrComparison {
  topIrr: string | null;
  selfFundingIrr: string | null;
}

const EMPTY: TopPolicyIrrComparison = { topIrr: null, selfFundingIrr: null };

/**
 * Resolve a single, reliable self-funding IRR to compare against.
 *
 * Only returns a value when the client has exactly one usable self-funding
 * source with a non-suspect IRR that is strictly lower than `topIrr`. With
 * multiple sources there is no honest single "blended" figure to cite, so we
 * fall back to no comparison rather than overstate it.
 */
function resolveSelfFundingIrr(client: Client, topIrr: number): string | null {
  const sources = Object.values(client.multipleFundingSources?.selfFunding ?? {});

  const usable = sources.filter(
    source =>
      source.hasSelfFunding &&
      !source.isDeactivated &&
      !source.isIrrSuspect &&
      typeof source.selfFundingProjectedInternalRateOfReturn === 'number' &&
      isFinite(source.selfFundingProjectedInternalRateOfReturn),
  );

  if (usable.length !== 1) return null;

  const selfIrr = usable[0].selfFundingProjectedInternalRateOfReturn as number;

  if (selfIrr >= topIrr) return null;

  return formatPercent(selfIrr, 2);
}

export function useTopPolicyIrr(clientId: string, enabled: boolean): TopPolicyIrrComparison {
  const client = useSelector(selectClient);
  const { session } = useSession();
  const { data: quotes } = useGuidedPlanningQuotes(clientId, session);

  return useMemo(() => {
    if (!enabled || !quotes?.length || !client) return EMPTY;

    try {
      const enriched = enrichQuotesWithROI(
        quotes as unknown as Parameters<typeof enrichQuotesWithROI>[0],
        client as unknown as Parameters<typeof enrichQuotesWithROI>[1],
        'internalRateOfReturn',
        { handleTraditionalLTC: true },
      );

      let bestRoiValues: (typeof enriched)[number]['roiValues'] | undefined;
      let bestIrr = -Infinity;

      for (const quote of enriched) {
        const irr = quote.roiValues?.projectedInternalRateOfReturn;

        if (irr === undefined) continue;

        if (irr > bestIrr) {
          bestIrr = irr;
          bestRoiValues = quote.roiValues;
        }
      }

      if (!bestRoiValues) return EMPTY;

      const text = formatROI(bestRoiValues, 'internalRateOfReturn');

      if (text === 'N/A' || text === IRR_SUSPECT_MESSAGE) return EMPTY;

      return {
        topIrr: text,
        selfFundingIrr: resolveSelfFundingIrr(client, bestIrr),
      };
    } catch {
      return EMPTY;
    }
  }, [enabled, quotes, client]);
}
